<script setup lang="ts">
import { ref, watch, onMounted, onBeforeUnmount } from 'vue'
import intlTelInput from 'intl-tel-input'
import type { Plugin } from 'intl-tel-input'
import 'intl-tel-input/build/js/utils.js'
import 'intl-tel-input/build/css/intlTelInput.css'

const props = withDefaults(defineProps<{
    modelValue?: string,
    initialCountry?: string,
}>(), {
    modelValue: '',
    initialCountry: 'ee',
})

const emits = defineEmits(['update:modelValue'])

const iti = ref<Plugin | null>(null)
const number = ref('')
const input = ref<HTMLInputElement | null>(null)

onMounted(() => {
    iti.value = intlTelInput(input.value!, {
        nationalMode: false,
        separateDialCode: true,
        // autoHideDialCode: true,
        formatOnDisplay: false,
        preferredCountries: ['ee', 'lv', 'lt', 'fi'],
        localizedCountries: {
            ee: 'Eesti',
            lv: 'Latvija',
            lt: 'Lietuva',
            fi: 'Suomi',
        },
        initialCountry: props.initialCountry,
    })

    input.value!.addEventListener("countrychange", onChange)

    iti.value!.setNumber(props.modelValue || '')
})

watch(() => props.modelValue, (value) => {
    iti.value!.setNumber(value)
})

watch(number, () => {
    onChange()
})

const onChange = () => {
    emits('update:modelValue', iti.value!.getNumber(intlTelInputUtils.numberFormat.E164))
}

onBeforeUnmount(() => {
    input.value!.removeEventListener("countrychange", onChange)

    iti.value!.destroy()
})
</script>

<template>
    <input
        ref="input"
        v-model="number"
        type="tel"
        class="block px-4 py-3 border rounded text-input-lg sm:text-input-base text-grayscale-800 border-grayscale-200 placeholder:text-grayscale-200 focus:ring-grayscale-900 focus:border-grayscale-900 disabled:bg-gray-50"
        maxlength="15">
</template>


<style>
.iti {
    width: 100%;
}

.iti__selected-flag {
    outline: none;
    border-radius: 0.25rem 0 0 0.25rem;
}

.iti__country-list {
    border-color: #e5e5e5;
}

.iti__divider {
    border-color: #e5e5e5;
}

.iti__country {
    font-size: 0.9375rem;
    line-height: 1.375rem;
    font-weight: 400;
    color: #111;
}

.iti__country.iti__highlight {
    background-color: #f5f5f5;
}

.iti__dial-code {
    color: #767676;
}

.iti__selected-flag {
    padding: 0 8px 0 8px;
}

.iti__selected-dial-code {
    font-size: 0.9375rem;
    line-height: 1.375rem;
    color: #232930;
}

.iti__arrow {
    border-top: 4px solid #232930;
    margin-left: 4px;
}

.iti__arrow.iti__arrow--up {
    border-top: none;
}

.iti--separate-dial-code .iti__selected-flag {
    background-color: #F4F4F5;
}

.iti--separate-dial-code .iti__selected-dial-code {
    margin-left: 4px;
}

.iti__flag-container {
    margin-right: 0.875rem;
}

.iti--allow-dropdown input,
.iti--allow-dropdown input[type=text],
.iti--allow-dropdown input[type=tel],
.iti--separate-dial-code input,
.iti--separate-dial-code input[type=text],
.iti--separate-dial-code input[type=tel] {
    padding-right: 0.875rem;
}
</style>
